<template>
    <div>
        <v-navigation-drawer :value="detalleOpen" @input="onDrawerInput" temporary right fixed
            :width="drawerWidth">
            <template v-slot:prepend>
                <div class="ds-head">
                    <div class="ds-head__row">
                        <div class="ds-head__title">
                            <span class="ds-title">{{ detailsVenta.tipoSalida?.nombre }} · {{ detailsVenta.ticketFolio }}</span>
                            <span class="ds-sub">{{ detailsVenta.registerDate }}</span>
                        </div>
                        <StatusChip v-if="chip" :label="chip.label" :tone="chip.tone" :color="chip.color" />
                        <v-btn icon small aria-label="Cerrar detalle" @click="close">
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </v-btn>
                    </div>
                    <div v-if="summary" class="ds-pay">
                        <div class="ds-bar" role="progressbar" aria-label="Porcentaje pagado"
                            aria-valuemin="0" aria-valuemax="100" :aria-valuenow="summary.percent">
                            <span class="ds-bar__fill" :style="{ width: summary.percent + '%' }"></span>
                        </div>
                        <div class="ds-pay__nums">
                            <span>Pagado <b>{{ money(summary.paid) }}</b></span>
                            <span>Saldo <b>{{ money(summary.balance) }}</b></span>
                            <span>Total <b>{{ money(summary.total) }}</b></span>
                        </div>
                    </div>
                </div>
            </template>

            <div class="ds-body">
                <section class="ds-card">
                    <h3 class="ds-label">Datos de la venta</h3>
                    <dl class="ds-meta">
                        <div>
                            <dt>Tipo de salida</dt>
                            <dd>{{ detailsVenta.tipoSalida?.nombre }}</dd>
                        </div>
                        <template v-if="!isModeProcess">
                            <div>
                                <dt>Cliente</dt>
                                <dd>{{ detailsVenta.cliente?.nombreComercial }}</dd>
                            </div>
                            <div>
                                <dt>Forma de pago</dt>
                                <dd>{{ detailsVenta.tipoPago?.formaPago }}</dd>
                            </div>
                            <div>
                                <dt>Moneda</dt>
                                <dd>{{ detailsVenta.tipoMoneda?.moneda }}</dd>
                            </div>
                            <div v-if="detailsVenta.clienteSucursal">
                                <dt>Sucursal</dt>
                                <dd>{{ detailsVenta.clienteSucursal?.name }}</dd>
                            </div>
                            <div v-if="detailsVenta.employee">
                                <dt>Empleado</dt>
                                <dd>{{ detailsVenta.employee?.nombreCompleto }}</dd>
                            </div>
                        </template>
                    </dl>
                </section>

                <section class="ds-card">
                    <h3 class="ds-label">Productos <span class="ds-hint">{{ itemVenta.length }} partidas</span></h3>
                    <ul class="ds-lines">
                        <li v-for="item in itemVenta" :key="item.id" class="ds-line">
                            <span class="ds-line__name">
                                {{ item.producto?.nombre }}
                                <span v-if="isModeProcess && item.newProducto" class="ds-line__proc">→ {{ item.newProducto.nombre }}</span>
                            </span>
                            <span class="ds-line__detail">
                                {{ item.cantidad }} {{ item.unidadSalida?.nombre }} · {{ item.kg }} kg × {{ money(item.precioVenta) }}
                            </span>
                            <span class="ds-line__total">{{ money(item.total) }}</span>
                        </li>
                    </ul>
                    <div class="ds-total">
                        <span>Total</span>
                        <span>{{ totalVentaNatural }}</span>
                    </div>
                </section>

                <section v-if="isSale" class="ds-card">
                    <button type="button" class="ds-acc" :aria-expanded="String(paymentsOpen)"
                        aria-controls="ds-payments" @click="paymentsOpen = !paymentsOpen">
                        <span class="ds-label ds-label--flush">Pagos <span class="ds-hint">{{ activePayments.length }} registrados</span></span>
                        <i class="fas ds-acc__icon" :class="paymentsOpen ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
                    </button>
                    <div v-show="paymentsOpen" id="ds-payments" class="ds-payments">
                        <p v-if="!payments.length" class="ds-empty">Esta venta todavía no tiene pagos.</p>
                        <ul v-else class="ds-pays">
                            <li v-for="item in payments" :key="item.ventaPago.id" class="ds-pay-row"
                                :class="{ 'ds-pay-row--void': item.ventaPago.eliminado }">
                                <span class="ds-pay-row__icon"><i class="fas fa-coins" aria-hidden="true"></i></span>
                                <span class="ds-pay-row__info">
                                    <span class="ds-pay-row__name">
                                        {{ item.ventaPago.folio }}
                                        <StatusChip v-if="item.ventaPago.eliminado" label="Cancelado" tone="danger" />
                                    </span>
                                    <span class="ds-pay-row__detail">{{ paymentDetail(item) }}</span>
                                </span>
                                <span class="ds-pay-row__amount">{{ item.payedAmountNatural }}</span>
                                <span class="ds-pay-row__actions">
                                    <v-btn icon aria-label="Ver pago en PDF"
                                        @click="previewPayment({ item, isWithPrinter: false })">
                                        <i class="fas fa-file-pdf" aria-hidden="true"></i>
                                    </v-btn>
                                    <v-btn icon aria-label="Imprimir pago en ticket"
                                        @click="previewPayment({ item, isWithPrinter: true })">
                                        <i class="fas fa-print" aria-hidden="true"></i>
                                    </v-btn>
                                </span>
                            </li>
                        </ul>
                        <v-btn v-if="payments.length" text small color="primary" class="ds-excel"
                            :loading="exportingExcel" @click="downloadExcel">
                            <i class="fas fa-file-excel mr-2" aria-hidden="true"></i>Reporte de pagos a Excel
                        </v-btn>
                    </div>
                </section>
            </div>

            <template v-slot:append>
                <div class="ds-foot">
                    <v-btn outlined small color="primary" :loading="loadingTicket"
                        @click="printTicket({ ventaId: detailsVenta.id, isWithPrinter: false })">
                        <i class="fas fa-file-pdf mr-2" aria-hidden="true"></i>Ver ticket
                    </v-btn>
                    <v-btn v-if="isSale" color="primary" small depressed :loading="loadingTicket"
                        @click="printTicket({ ventaId: detailsVenta.id, isWithPrinter: true })">
                        <i class="fas fa-print mr-2" aria-hidden="true"></i>Imprimir ticket
                    </v-btn>
                </div>
            </template>
        </v-navigation-drawer>

        <PreviewPDF :pdfSource="pdfSource" :pdfBlob="pdfBlob" :title-pdf="titlePdf"
            :handlePdfModal="handlePdfModal" :dialog="showPdf" v-if="showPdf" />
    </div>
</template>
<script>
import VentaItemDataService from '@/service/venta/VentaDetalleDataService';
import VentaPagoDetalleDataService from '@/service/venta/pago/VentaPagoDetalleDataService';
import VentaPagoDataService from '@/service/venta/pago/VentaPagoDataService';
import VentaDataService from '@/service/venta/VentaDataService';
import PreviewPDF from '@/components/shared/PreviewPDF.vue';
import StatusChip from '@/components/shared/StatusChip.vue';
import { getFormatCurrency, toNumber } from '@/utils/CurrencyUtil';
import { extractMessage } from '@/helpers/errorHelper';
import { paymentSummary, headerChip, isActivePayment } from './ventaDetailSummary';

/**
 * Detalle de una venta/salida en un panel lateral (mismo patrón que
 * EntradaFilterDrawer). Los padres le pasan `detalleOpen` y `handleModal`
 * (alterna la visibilidad), igual que con el diálogo anterior.
 */
export default {
    name: "DetalleSale",
    props: {
        detalleOpen: { type: Boolean, default: false },
        handleModal: { type: Function },
        detailsVenta: { type: Object, default: () => ({}) },
    },
    components: { PreviewPDF, StatusChip },
    created() {
        this.detalleItemVenta();
        this.getPagos();
    },
    mounted() {
        // El botón que abre el detalle vive en una celda de la tabla, y la
        // primera columna es `sticky` con z-index: eso crea un contexto de
        // apilamiento que deja el panel (fixed) detrás de la barra superior.
        // Igual que `v-dialog`, se cuelga de la raíz de la app.
        const root = document.querySelector('[data-app]') || document.body;
        root.appendChild(this.$el);
    },
    beforeDestroy() {
        this.cleanupPdf();
        if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el);
    },
    data() {
        return {
            itemVenta: [],
            payments: [],
            // Hasta que llegan los pagos no se conoce el saldo: no se dibuja.
            paymentsLoaded: false,
            paymentsOpen: false,
            exportingExcel: false,
            loadingTicket: false,
            // to PDF
            pdfBlob: '',
            pdfSource: null,
            showPdf: false,
            titlePdf: 'VENTA',
        }
    },
    methods: {
        money(value) {
            return getFormatCurrency(toNumber(value, 0), '$ ');
        },
        paymentDetail(item) {
            return [item.ventaPago.paymentDateNatural, item.ventaPago.paymentMethodNatural, item.ventaPago.operationNumber]
                .filter(Boolean).join(' · ');
        },
        onDrawerInput(open) {
            if (!open) this.close();
        },
        close() {
            if (this.handleModal) this.handleModal();
            this.$emit('update:detalleOpen', false);
        },
        async detalleItemVenta() {
            try {
                const resp = await VentaItemDataService.showDetailsVenta(this.detailsVenta.id);
                this.itemVenta = resp.data;
            } catch (err) {
                this.$swal.fire({ icon: 'error', title: 'Error al obtener los datos', text: extractMessage(err), showConfirmButton: false, timer: 1500 });
            }
        },
        async getPagos() {
            try {
                const resp = await VentaPagoDetalleDataService.findByVenta(this.detailsVenta.id);
                this.payments = resp.data;
                this.paymentsLoaded = true;
            } catch (err) {
                this.$swal.fire({ icon: 'error', title: 'Error al obtener los pagos', text: extractMessage(err), showConfirmButton: false, timer: 1500 });
            }
        },
        async downloadExcel() {
            if (this.exportingExcel) return;
            this.exportingExcel = true;
            try {
                const response = await VentaPagoDetalleDataService.downloadExcelPagoDetalle({
                    ventaId: this.detailsVenta.id,
                    ventaPagoId: 0
                });
                if (response.status === 204) {
                    this.$swal.fire({ icon: 'error', text: 'No se encontraron detalles de pagos', showConfirmButton: false, timer: 3500 });
                    return;
                }
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Detalles_de_pagos_${this.detailsVenta.ticketFolio || ''}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                this.$swal.fire({ icon: 'error', text: 'Error al generar el reporte: ' + extractMessage(err), showConfirmButton: false, timer: 3500 });
            } finally {
                this.exportingExcel = false;
            }
        },
        showPdfBlob(data, title) {
            this.cleanupPdf();
            this.titlePdf = title;
            this.pdfBlob = new Blob([data], { type: 'application/pdf' });
            this.pdfSource = URL.createObjectURL(this.pdfBlob);
            this.showPdf = true;
        },
        async printTicket({ ventaId, isWithPrinter = false }) {
            if (this.loadingTicket) return;
            this.loadingTicket = true;
            try {
                const response = await VentaDataService.printTicket(ventaId, { isWithPrinter });
                this.showPdfBlob(response.data, (isWithPrinter ? 'TICKET ' : 'VENTA ') + this.detailsVenta.ticketFolio);
            } catch (error) {
                this.$swal.fire({ title: 'Error', text: 'No se pudo procesar la petición, ' + extractMessage(error), icon: 'error', showConfirmButton: false, timer: 2000 });
            } finally {
                this.loadingTicket = false;
            }
        },
        async previewPayment({ item, isWithPrinter = false }) {
            try {
                const params = { isWithPrinter, ventaId: this.detailsVenta.id, paymentId: item.ventaPago.id };
                const response = await VentaPagoDataService.printPayment(params);
                this.showPdfBlob(response.data, item.ventaPago.folio);
            } catch (error) {
                this.$swal.fire({ icon: 'error', text: 'Error al generar el reporte: ' + extractMessage(error), showConfirmButton: false, timer: 3500 });
            }
        },
        handlePdfModal() {
            this.showPdf = false;
            this.cleanupPdf();
        },
        cleanupPdf() {
            if (this.pdfSource) {
                URL.revokeObjectURL(this.pdfSource);
                this.pdfSource = null;
            }
            this.pdfBlob = null;
        }
    },
    computed: {
        drawerWidth() {
            return this.$vuetify.breakpoint.xsOnly ? '100%' : 540;
        },
        isSale() {
            const nombre = this.detailsVenta.tipoSalida?.nombre;
            return nombre == 'Venta' || nombre == 'Venta interna';
        },
        isModeProcess() { return this.detailsVenta.tipoSalida?.clave == 'SPR'; },
        activePayments() { return this.payments.filter(isActivePayment); },
        summary() {
            if (!this.isSale || !this.paymentsLoaded) return null;
            return paymentSummary(this.detailsVenta.total, this.payments);
        },
        chip() {
            return headerChip({ sale: this.detailsVenta, isSale: this.isSale, summary: this.summary });
        },
        totalVentaNatural() {
            return this.money(this.detailsVenta.total);
        },
    }
}
</script>
<style scoped>
.ds-head {
    background: var(--app-surface);
    padding: 12px 16px 14px;
    border-bottom: 1px solid var(--app-line);
}
.ds-head__row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.ds-head__title {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}
.ds-title {
    font-size: 16px;
    font-weight: 600;
    overflow-wrap: anywhere;
}
.ds-sub {
    font-size: 12px;
    color: var(--app-text-muted);
}
.ds-pay {
    padding-top: 12px;
}
.ds-bar {
    height: 6px;
    border-radius: 3px;
    background: var(--app-field);
    overflow: hidden;
}
.ds-bar__fill {
    display: block;
    height: 100%;
    background: var(--app-primary);
}
.ds-pay__nums {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 4px 12px;
    padding-top: 6px;
    font-size: 12px;
    color: var(--app-text-muted);
    font-variant-numeric: tabular-nums;
}
.ds-pay__nums b {
    color: var(--app-text);
    font-weight: 600;
}
.ds-body {
    background: var(--app-surface-sunken);
    padding: 12px 16px;
    min-height: 100%;
}
.ds-card {
    background: var(--app-surface);
    border: 1px solid var(--app-line);
    border-radius: 12px;
    padding: 4px 14px 12px;
    margin-bottom: 12px;
}
.ds-label {
    font-size: 13px;
    font-weight: 700;
    color: var(--app-text-muted);
    padding: 12px 0 6px;
    display: flex;
    justify-content: space-between;
}
.ds-label--flush {
    padding: 0;
    flex: 1;
}
.ds-hint {
    font-weight: 400;
}
.ds-meta > div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 3px 0;
    font-size: 13px;
}
.ds-meta dt {
    color: var(--app-text-muted);
}
.ds-meta dd {
    text-align: right;
    overflow-wrap: anywhere;
}
.ds-lines {
    list-style: none;
    padding: 0;
}
.ds-line {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 2px 12px;
    padding: 8px 0;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    border-bottom: 1px solid var(--app-line);
}
.ds-line__name {
    font-weight: 500;
    overflow-wrap: anywhere;
}
.ds-line__proc {
    color: var(--app-primary);
    font-weight: 500;
}
.ds-line__detail {
    grid-column: 1;
    font-size: 12px;
    color: var(--app-text-muted);
}
.ds-line__total {
    grid-row: 1 / 3;
    grid-column: 2;
    align-self: center;
    font-weight: 500;
}
.ds-total {
    display: flex;
    justify-content: space-between;
    padding-top: 10px;
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}
.ds-acc {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 44px;
    text-align: left;
}
.ds-acc__icon {
    padding-left: 8px;
    font-size: 12px;
    color: var(--app-text-muted);
}
.ds-empty {
    font-size: 13px;
    color: var(--app-text-muted);
    padding: 4px 0;
}
.ds-pays {
    list-style: none;
    padding: 0;
}
.ds-pay-row {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    font-size: 13px;
    border-bottom: 1px solid var(--app-line);
}
.ds-pay-row__icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--app-primary-soft);
    color: var(--app-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
}
.ds-pay-row__info {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
.ds-pay-row__name {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    overflow-wrap: anywhere;
}
.ds-pay-row__detail {
    font-size: 12px;
    color: var(--app-text-muted);
    overflow-wrap: anywhere;
}
.ds-pay-row__amount {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
}
.ds-pay-row--void .ds-pay-row__amount {
    text-decoration: line-through;
    color: var(--app-text-muted);
}
.ds-pay-row__actions {
    display: flex;
}
.ds-excel {
    margin-top: 8px;
}
.ds-foot {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid var(--app-line-strong);
    background: var(--app-surface);
}
@media (max-width: 400px) {
    .ds-pay-row {
        grid-template-columns: 28px minmax(0, 1fr) auto;
    }
    .ds-pay-row__actions {
        grid-column: 2 / 4;
    }
}
</style>
